import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Sparkles, Share2, Bookmark, Instagram, Facebook, Twitter, Link2, Check, ArrowRight, TrendingUp, TrendingDown, Home, Building2, Users, BarChart3, PoundSterling, Percent, Target, Star } from "lucide-react";

const DISCLAIMER = "Informational observations, not advice.";

// Circular gauge for potential
function PotentialRing({ band }) {
  const percentage = band === "high" ? 85 : band === "medium" ? 60 : 35;
  const color = band === "high" ? "#006A4D" : band === "medium" ? "#C5A059" : "#6B7280";
  const label = band === "high" ? "HIGH" : band === "medium" ? "MODERATE" : "LIMITED";
  
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  return (
    <div className="relative w-24 h-24 flex-shrink-0">
      <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
        <circle cx="50" cy="50" r={radius} fill="none" stroke="#E5E7EB" strokeWidth="6" />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: "stroke-dashoffset 1s ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold text-cipher-charcoal">{percentage}</span>
        <span className="text-[10px] font-semibold" style={{ color }}>{label}</span>
      </div>
    </div>
  );
}

// Section divider with icon
function SectionHeader({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-cipher-green" />
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</span>
    </div>
  );
}

// Main shareable insight card - Enhanced with all content
export function InsightCard({ 
  firstName, 
  postcode, 
  potentialBand, 
  marketData,
  propertyDescription,
  similarPropertiesInsight,
  areaComparison,
  keyFinding,
  bedrooms,
  dwellingType,
  onShare,
  onSave,
  className = ""
}) {
  const [copied, setCopied] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  // Generate property title from details
  const getPropertyTitle = () => {
    if (bedrooms && dwellingType) {
      const typeMap = {
        "detached": "Detached",
        "semi-detached": "Semi-Detached",
        "terraced": "Terraced",
        "flat": "Flat",
        "bungalow": "Bungalow",
        "maisonette": "Maisonette"
      };
      const typeLabel = typeMap[dwellingType.toLowerCase()] || dwellingType;
      return `Your ${bedrooms}-bed ${typeLabel} home in ${postcode?.substring(0, 4) || "your area"}`;
    }
    if (postcode) {
      return `Your property in ${postcode}`;
    }
    return "Your Property";
  };

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = `I just discovered my property's hidden potential with HiPPO! ${potentialBand.toUpperCase()} potential detected in ${postcode}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const socialLinks = [
    {
      name: "Instagram",
      icon: Instagram,
      action: () => window.open(`https://www.instagram.com/`, "_blank")
    },
    {
      name: "Facebook", 
      icon: Facebook,
      action: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank")
    },
    {
      name: "Twitter",
      icon: Twitter,
      action: () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank")
    },
    {
      name: "Copy Link",
      icon: copied ? Check : Link2,
      action: handleCopy
    }
  ];

  // Format price
  const formatPrice = (price) => {
    if (!price) return "N/A";
    if (price >= 1000000) return `£${(price / 1000000).toFixed(2)}M`;
    return `£${(price / 1000).toFixed(0)}K`;
  };

  return (
    <Card className={`relative overflow-hidden bg-gradient-to-br from-white via-white to-cipher-bone/30 border border-gray-100 shadow-xl ${className}`}>
      {/* Social action bar */}
      <div className="absolute top-4 right-4 flex gap-2 z-10">
        <button
          onClick={() => setShowShareMenu(!showShareMenu)}
          className="p-2.5 rounded-full bg-white hover:bg-gray-50 shadow-md border border-gray-100 transition-all hover:scale-105"
          data-testid="share-card-btn"
        >
          <Share2 className="w-5 h-5 text-gray-600" />
        </button>
        <button
          onClick={onSave}
          className="p-2.5 rounded-full bg-white hover:bg-gray-50 shadow-md border border-gray-100 transition-all hover:scale-105"
          data-testid="save-card-btn"
        >
          <Bookmark className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Share menu dropdown */}
      {showShareMenu && (
        <div className="absolute top-16 right-4 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-20 min-w-[160px]">
          {socialLinks.map((social) => (
            <button
              key={social.name}
              onClick={() => { social.action(); setShowShareMenu(false); }}
              className="flex items-center gap-3 w-full px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <social.icon className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">{social.name}</span>
            </button>
          ))}
        </div>
      )}

      <div className="p-5 sm:p-6">
        {/* Header with location and potential gauge */}
        <div className="flex items-start gap-4 mb-5 pr-20">
          <PotentialRing band={potentialBand} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
              <MapPin className="w-3 h-3" />
              <span>{postcode}</span>
            </div>
            <h2 className="text-lg font-bold text-cipher-charcoal mb-1 truncate">
              {getPropertyTitle()}
            </h2>
            <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
              potentialBand === "high" ? "bg-cipher-green/10 text-cipher-green" :
              potentialBand === "medium" ? "bg-cipher-gold/10 text-cipher-gold" :
              "bg-gray-100 text-gray-600"
            }`}>
              <Star className="w-3 h-3" />
              {potentialBand === "high" ? "High Potential" : potentialBand === "medium" ? "Moderate Potential" : "Limited Potential"}
            </div>
          </div>
        </div>

        {/* Key Finding - Highlighted */}
        {keyFinding && (
          <div className="bg-gradient-to-r from-cipher-green/10 to-cipher-gold/5 rounded-xl p-4 mb-5 border border-cipher-green/20">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-cipher-green flex items-center justify-center flex-shrink-0">
                <Target className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-cipher-charcoal text-sm mb-1">Key Finding</h4>
                <p className="text-sm text-gray-700">{keyFinding}</p>
              </div>
            </div>
          </div>
        )}

        {/* Market Context */}
        {marketData && (
          <div className="mb-5">
            <SectionHeader icon={BarChart3} title="Market Context" />
            <div className="grid grid-cols-3 gap-2">
              {marketData.avg_price && (
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <PoundSterling className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                  <p className="text-sm font-bold text-cipher-charcoal">{formatPrice(marketData.avg_price)}</p>
                  <p className="text-[10px] text-gray-500">Avg Price</p>
                </div>
              )}
              {marketData.price_change_1y !== null && marketData.price_change_1y !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  {marketData.price_change_1y >= 0 ? 
                    <TrendingUp className="w-4 h-4 text-cipher-green mx-auto mb-1" /> :
                    <TrendingDown className="w-4 h-4 text-red-500 mx-auto mb-1" />
                  }
                  <p className={`text-sm font-bold ${marketData.price_change_1y >= 0 ? "text-cipher-green" : "text-red-500"}`}>
                    {marketData.price_change_1y >= 0 ? "+" : ""}{marketData.price_change_1y.toFixed(1)}%
                  </p>
                  <p className="text-[10px] text-gray-500">1Y Change</p>
                </div>
              )}
              {marketData.rental_yield && (
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <Percent className="w-4 h-4 text-cipher-gold mx-auto mb-1" />
                  <p className="text-sm font-bold text-cipher-charcoal">{marketData.rental_yield.toFixed(1)}%</p>
                  <p className="text-[10px] text-gray-500">Yield</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Property Description */}
        {propertyDescription && (
          <div className="mb-5">
            <SectionHeader icon={Home} title="Property Assessment" />
            <p className="text-sm text-gray-700 leading-relaxed">{propertyDescription}</p>
          </div>
        )}

        {/* Similar Properties Insight */}
        {similarPropertiesInsight && (
          <div className="mb-5">
            <SectionHeader icon={Building2} title="Similar Properties" />
            <p className="text-sm text-gray-700 leading-relaxed">{similarPropertiesInsight}</p>
          </div>
        )}

        {/* Area Comparison - Text Only */}
        {areaComparison && (
          <div className="mb-4">
            <SectionHeader icon={Users} title="Area Comparison" />
            <p className="text-sm text-gray-700 leading-relaxed">{areaComparison}</p>
          </div>
        )}

        {/* Disclaimer */}
        <div className="pt-3 border-t border-gray-100">
          <p className="text-[10px] text-gray-400 text-center">{DISCLAIMER}</p>
        </div>
      </div>
    </Card>
  );
}

// Unlock CTA component
export function UnlockCTA({ onUnlock, loading, className = "" }) {
  return (
    <div className={`text-center ${className}`}>
      <Button
        onClick={onUnlock}
        className="cipher-btn-primary w-full max-w-sm mb-3"
        disabled={loading}
        data-testid="unlock-insight-btn"
      >
        {loading ? (
          <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            Unlock Full Insight
            <ArrowRight className="w-4 h-4 ml-2" />
          </>
        )}
      </Button>
      <p className="text-sm text-gray-600 mb-2">
        Includes detailed report and 1-1 conversation
      </p>
      <p className="text-xs text-gray-400">
        Most people spend about 30 mins reviewing their full report
      </p>
    </div>
  );
}

export default InsightCard;
